// theme/assets/frame-picker.js
//
// Handlar interaktionen i snippets/frame-picker.liquid:
//   1. När kundens canvas-storlek ändras → uppdatera compat-state
//      (cirklar struck om size inte i FRAME_COMPATIBLE_SIZES).
//   2. När en ramfärg väljs → swappa canvas-PDP:ns hero-bild till
//      ramproduktens featured_image (window.__frameImages[color]).
//   3. När kunden klickar "Lägg i kundvagn" och en ramfärg är vald →
//      hooka submit, lägg canvas + ram via /cart/add.js (bulk add).
//
// Bilder läses från window.__frameImages (genererat av Liquid från
// ramprodukternas featured_image), INTE från AI-genererade
// frame-edge-mockups som tidigare. Strategin ändrades 2026-05-04.

(function () {
  'use strict';

  // Storleksguide-popup lämnas på sin default-position (egen rad efter
  // variant-knapparna). Försök att hoista inline med "Storlek:"-rubriken
  // visade sig fragilt cross-browser pga <legend>-elementets special-
  // styling (display:table från app.css + flex/float-quirks).

  const root = document.querySelector('[data-frame-picker]');
  if (!root) return;

  const FRAME_COMPATIBLE_SIZES = new Set([
    '70x100','100x70','80x80','90x130','130x90','100x100'
  ]);

  // Hero-bilden: leta efter den aktiva sliden i Habitat-temats produktslider.
  // Habitat strukturerar product gallery som .product-images__slide(.is-selected).
  const heroSlide = document.querySelector('.product-images__slide.is-selected, .product-images__slide.is-initial-selected');
  const heroImg = heroSlide ? heroSlide.querySelector('img') : null;
  const originalHeroSrc = heroImg ? heroImg.getAttribute('src') : null;
  const originalHeroSrcset = heroImg ? heroImg.getAttribute('srcset') : null;

  let currentFrame = 'none';

  function normalizeSize(raw) {
    if (!raw) return '';
    return String(raw).toLowerCase().replace(/\s|cm/g, '').replace('×', 'x');
  }

  function formatPrice(cents) {
    // Picker-knappens prisbelopp — lättviktig SEK-formattering utan decimaler.
    // Översatt prefix ("+ {{ price }}" / "från {{ price }}") läses från
    // data-attributes på root och appliceras i applyAddon/applyFrom.
    const sek = (cents / 100).toFixed(0);
    return `${sek} kr`;
  }

  function applyPriceTemplate(template, formattedPrice) {
    if (!template) return formattedPrice;
    return template.replace(/\{\{\s*price\s*\}\}/g, formattedPrice);
  }

  function withThousandsSep(numStr, sep) {
    const parts = numStr.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    return parts.join('.');
  }

  // Implementerar Shopify-likt money_format-format för totalprisuppdateringen
  // i .price-blocket (måste matcha Habitats befintliga formattering så det inte
  // ser ut som ett oväntat formatbyte när användaren klickar).
  function formatShopifyMoney(cents, formatString) {
    const fmt = formatString || '{{ amount }} kr';
    const major = cents / 100;
    if (fmt.indexOf('amount_no_decimals_with_comma_separator') !== -1) {
      return fmt.replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g,
        withThousandsSep(major.toFixed(0), '.'));
    }
    if (fmt.indexOf('amount_no_decimals_with_space_separator') !== -1) {
      return fmt.replace(/\{\{\s*amount_no_decimals_with_space_separator\s*\}\}/g,
        withThousandsSep(major.toFixed(0), ' '));
    }
    if (fmt.indexOf('amount_with_comma_separator') !== -1) {
      const withDot = withThousandsSep(major.toFixed(2), '.');
      // Komma som decimal: "1.234.56" → "1.234,56" — byt sista punkt till komma.
      const swapped = withDot.replace(/\.(\d{2})$/, ',$1');
      return fmt.replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, swapped);
    }
    if (fmt.indexOf('amount_with_space_separator') !== -1) {
      return fmt.replace(/\{\{\s*amount_with_space_separator\s*\}\}/g,
        withThousandsSep(major.toFixed(2), ' '));
    }
    if (fmt.indexOf('amount_no_decimals') !== -1) {
      return fmt.replace(/\{\{\s*amount_no_decimals\s*\}\}/g, major.toFixed(0));
    }
    return fmt.replace(/\{\{\s*amount\s*\}\}/g, major.toFixed(2));
  }

  function getSelectedCanvasVariant() {
    const variantsScript = document.querySelector('variant-selects script[type="application/json"]');
    const variantInput = document.querySelector('[name="id"]');
    if (!variantsScript || !variantInput) return null;
    try {
      const variants = JSON.parse(variantsScript.textContent);
      return variants.find((v) => String(v.id) === String(variantInput.value)) || null;
    } catch (err) {
      return null;
    }
  }

  function applyTotalPrice() {
    const canvas = getSelectedCanvasVariant();
    if (!canvas || typeof canvas.price !== 'number') return;
    const canvasSize = root.getAttribute('data-canvas-size');
    const variants = window.__frameVariants || {};
    const frame = currentFrame !== 'none'
      ? (variants[currentFrame] && variants[currentFrame][canvasSize])
      : null;
    const framePrice = frame && typeof frame.price === 'number' ? frame.price : 0;

    // Aktuellt pris (<ins>): canvas.price + ev. ramens pris.
    const totalCents = canvas.price + framePrice;
    const totalFormatted = formatShopifyMoney(totalCents, window.__frameMoneyFormat);
    document.querySelectorAll('[id^="price-"] ins .amount').forEach((node) => {
      node.textContent = totalFormatted;
    });

    // Compare-at (<del>, överstruket originalpris): bara uppdatera om
    // canvas-varianten har compare_at_price (= på rea). Ramen är aldrig
    // på rea, så ramens pris adderas oavsett.
    // Utan denna fix visade compare-at bara canvas-original — det såg
    // ut som om ramen var "gratis" jämfört med originalet.
    if (typeof canvas.compare_at_price === 'number' && canvas.compare_at_price > 0) {
      const compareCents = canvas.compare_at_price + framePrice;
      const compareFormatted = formatShopifyMoney(compareCents, window.__frameMoneyFormat);
      document.querySelectorAll('[id^="price-"] del .amount').forEach((node) => {
        node.textContent = compareFormatted;
      });
    }
  }

  function getMinPrice(color) {
    const variants = (window.__frameVariants || {})[color] || {};
    const prices = Object.values(variants)
      .map((v) => (v && typeof v.price === 'number' ? v.price : null))
      .filter((p) => p !== null);
    return prices.length ? Math.min.apply(null, prices) : null;
  }

  function updatePriceLabels(canvasSize) {
    const variants = window.__frameVariants || {};
    const tplAddon = root.getAttribute('data-price-addon') || '+ {{ price }}';
    const tplFrom = root.getAttribute('data-price-from') || 'from {{ price }}';
    ['ljus-ek','valnot'].forEach((color) => {
      const node = root.querySelector(`[data-frame-price="${color}"]`);
      if (!node) return;
      const exact = variants[color] && variants[color][canvasSize];
      if (exact && typeof exact.price === 'number') {
        node.textContent = applyPriceTemplate(tplAddon, formatPrice(exact.price));
      } else {
        const min = getMinPrice(color);
        node.textContent = min !== null
          ? applyPriceTemplate(tplFrom, formatPrice(min))
          : '';
      }
    });
  }

  function syncCanvasSize() {
    const variantInput = document.querySelector('[name="id"]');
    let size = '';

    if (variantInput) {
      const variantId = variantInput.value;
      // Habitat exposerar product.variants som JSON i variant-selects > script
      const variantsScript = document.querySelector('variant-selects script[type="application/json"]');
      if (variantsScript) {
        try {
          const variants = JSON.parse(variantsScript.textContent);
          const match = variants.find((v) => String(v.id) === String(variantId));
          if (match) size = normalizeSize(match.option1);
        } catch (err) {
          // ignorera parse-fel — fallback nedan
        }
      }
    }

    // Fallback: läs från första aktiva variant_radio/select.
    if (!size) {
      const activeOption = document.querySelector('input[name^="options"]:checked, select[name^="options"]');
      if (activeOption) size = normalizeSize(activeOption.value);
    }

    root.setAttribute('data-canvas-size', size);
    const compatible = FRAME_COMPATIBLE_SIZES.has(size);
    root.setAttribute('data-compatible', String(compatible));

    root.querySelectorAll('.frame-picker__option:not(.frame-picker__option--none)').forEach((btn) => {
      btn.classList.toggle('is-disabled', !compatible);
      btn.setAttribute('aria-disabled', String(!compatible));
      if (!compatible) {
        btn.setAttribute('tabindex', '-1');
      } else {
        btn.removeAttribute('tabindex');
      }
    });

    let hint = root.querySelector('.frame-picker__hint');
    if (!compatible && !hint) {
      hint = document.createElement('p');
      hint.className = 'frame-picker__hint';
      hint.textContent = root.getAttribute('data-hint-unavailable') || '';
      root.appendChild(hint);
    } else if (compatible && hint) {
      hint.remove();
    }

    updatePriceLabels(size);

    if (!compatible && currentFrame !== 'none') {
      selectFrame('none');
    } else {
      applyTotalPrice();
      syncBuyNowVisibility();
    }
  }

  function selectFrame(color) {
    currentFrame = color;

    root.querySelectorAll('.frame-picker__option').forEach((btn) => {
      const isSelected = btn.dataset.frameColor === color;
      btn.classList.toggle('is-selected', isSelected);
      btn.setAttribute('aria-pressed', String(isSelected));
    });

    // Uppdatera heading-värdet ("Ram: <vald-färg>") så det matchar
    // Habitats variant-picker-mönster ("Storlek: 70x100 cm"). Texten
    // hämtas från knappens data-frame-display-name (sätts via Liquid t-helper).
    const selectedBtn = root.querySelector(`.frame-picker__option[data-frame-color="${color}"]`);
    const displayName = selectedBtn?.getAttribute('data-frame-display-name') || '';
    const labelNode = root.querySelector('[data-frame-current-label]');
    if (labelNode && displayName) labelNode.textContent = displayName;

    if (heroImg) {
      if (color === 'none') {
        if (originalHeroSrc) heroImg.setAttribute('src', originalHeroSrc);
        if (originalHeroSrcset !== null) {
          if (originalHeroSrcset) heroImg.setAttribute('srcset', originalHeroSrcset);
          else heroImg.removeAttribute('srcset');
        }
      } else {
        const url = (window.__frameImages || {})[color];
        if (url) {
          heroImg.setAttribute('src', url);
          // srcset på orig-bilden kan peka på canvas-CDN:n och ta över src — rensa.
          heroImg.removeAttribute('srcset');
        }
      }
    }

    applyTotalPrice();
    syncBuyNowVisibility();
  }

  root.addEventListener('click', (event) => {
    const btn = event.target.closest('.frame-picker__option');
    if (!btn || !root.contains(btn)) return;
    if (btn.classList.contains('is-disabled')) {
      event.preventDefault();
      return;
    }
    selectFrame(btn.dataset.frameColor);
  });

  // ─── CART ADD HOOK ──────────────────────────────────────────────────
  //
  // Habitats <product-form> (assets/product.js rad 559+) registrerar en
  // submit-handler i constructor som preventDefault + fetchar /cart/add
  // med canvas-only. Vi MÅSTE köra först och blockera den när en ram
  // är vald.
  //
  // Strategi (lärt av tidigare misslyckad fix på productForm-element):
  //   1. Lyssna på `submit` på `document` i CAPTURE phase. Capture körs
  //      från document → target, så vi fångar alla submits oavsett vilket
  //      form-element det är. Mindre känsligt för selector-mismatches.
  //   2. stopImmediatePropagation() blockerar Habitats handler.
  //   3. För dynamic checkout-knappen ("Köp nu" / Shop Pay) — den triggar
  //      INTE submit, går runt oss helt. Vi DÖLJER den när ram är vald
  //      (CSS via .frame-picker-buy-now-hidden på body) och visar text om
  //      att kunden måste använda "Lägg i kundvagn".

  function isCartAddForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    const action = form.getAttribute('action') || '';
    if (/\/cart\/add(\?|$)/.test(action)) return true;
    if (form.closest && form.closest('product-form')) return true;
    if (form.dataset && form.dataset.type === 'add-to-cart-form') return true;
    return false;
  }

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!isCartAddForm(form)) return;
    if (currentFrame === 'none') return; // canvas-only — låt Habitat hantera

    const canvasSize = root.getAttribute('data-canvas-size');
    const variants = window.__frameVariants || {};
    const frameVariant = variants[currentFrame] && variants[currentFrame][canvasSize];

    if (!frameVariant || !frameVariant.id) {
      console.error('[frame-picker] Saknar ram-variant för', currentFrame, canvasSize);
      return; // Habitat fortsätter med canvas-only
    }

    // Blockera Habitats canvas-only-fetch
    event.preventDefault();
    event.stopImmediatePropagation();
    event.stopPropagation();

    const formData = new FormData(form);
    const canvasVariantId = formData.get('id');
    const canvasQty = parseInt(formData.get('quantity') || '1', 10);

    if (!canvasVariantId) {
      console.error('[frame-picker] [name="id"] saknas/disabled i formData — kan inte addera');
      alert('Kunde inte läsa vald variant. Försök igen.');
      return;
    }

    const productTitle = (document.querySelector('.product-single__title, h1.product__title')?.textContent || '').trim();

    const items = [
      { id: canvasVariantId, quantity: canvasQty },
      {
        id: frameVariant.id,
        quantity: canvasQty,
        properties: {
          '_canvas_companion_variant': String(canvasVariantId),
          '_canvas_companion_label': productTitle ? `${productTitle} ${canvasSize}` : canvasSize
        }
      }
    ];

    // Skicka med `sections` så Shopify returnerar uppdaterad cart-HTML i
    // samma response — då kan vi anropa Habitats renderContents() och få
    // cart-drawern att öppnas direkt (samma flow som vid canvas-only
    // add-to-cart). Sektionsnamnen kommer från Habitats getSectionsToRender
    // i assets/product.js rad 620-636.
    const sections = ['main-cart', 'cart-drawer', 'cart-bubble'];

    try {
      const resp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items, sections, sections_url: window.location.pathname })
      });
      const respBody = await resp.text();
      if (!resp.ok) throw new Error(`HTTP ${resp.status}: ${respBody}`);

      const parsedState = JSON.parse(respBody);

      // Kalla Habitats <product-form>-instans renderContents — den
      // ersätter cart-drawer-DOM med ny innehåll och öppnar drawern via
      // `new CartDrawer()`-anropet i sin loop. Samma flow som vid
      // canvas-only submit, så UX är konsistent.
      const productFormEl = form.closest('product-form') || document.querySelector('product-form');
      if (productFormEl && typeof productFormEl.renderContents === 'function') {
        productFormEl.renderContents(parsedState);
      } else {
        // Fallback: navigera till /cart om Habitat-elementet inte är
        // upgraded än (osannolikt, men vi tappar inte ramen då).
        console.warn('[frame-picker] product-form.renderContents saknas — fallback till /cart');
        window.location.href = '/cart';
      }
    } catch (err) {
      console.error('[frame-picker] Cart bulk add failed', err);
      alert('Något gick fel när vi lade tavlan + ramen i kundvagnen. Försök igen, eller välj "Ingen" som ramfärg och köp ramen separat.');
    }
  }, true); // capture på document — mest pålitlig position

  // ─── DYNAMIC CHECKOUT ("Köp nu") — gömma när ram är vald ────────────
  //
  // Shopifys dynamic checkout-knapp triggar inte form submit; den hoppar
  // direkt till checkout med bara den valda canvas-varianten. När en ram
  // är vald måste vi förhindra detta — annars går ramen förlorad i kassan.
  // Lösning: dölj knappen via body-klass + CSS i frame-picker.css.
  function syncBuyNowVisibility() {
    if (currentFrame === 'none') {
      document.body.classList.remove('frame-picker-buy-now-hidden');
    } else {
      document.body.classList.add('frame-picker-buy-now-hidden');
    }
  }

  // Variant-byten: Habitat firar `product:variant-change` (verifierat i
  // assets/product.js rad 39-42 — dispatchCustomEvent). setTimeout 50ms
  // säkerställer att vår totalpris-override landar EFTER Habitats egen
  // price-render som triggas i samma change-event-cykel.
  document.addEventListener('product:variant-change', () => setTimeout(syncCanvasSize, 50));
  const variantInput = document.querySelector('[name="id"]');
  if (variantInput) {
    variantInput.addEventListener('change', () => setTimeout(syncCanvasSize, 50));
  }
  // Habitats variant-radios
  document.querySelectorAll('input[name^="options"]').forEach((el) => {
    el.addEventListener('change', () => setTimeout(syncCanvasSize, 50));
  });
  document.querySelectorAll('select[name^="options"]').forEach((el) => {
    el.addEventListener('change', () => setTimeout(syncCanvasSize, 50));
  });

  syncCanvasSize();
})();
